import { applySrcDoc, buildSrcDoc } from "../core/render";
import type { PreviewViewport, Orientation, ViewMode, ZoomLevel } from "../types";

export interface GridView {
  devices: PreviewViewport[];
  mode: ViewMode;
  orientation: Orientation;
  zoom: ZoomLevel;
  focusId: string | null;
  compareIds: string[];
}

function viewport(device: PreviewViewport, orientation: Orientation): { w: number; h: number } {
  return orientation === "landscape"
    ? { w: device.height, h: device.width }
    : { w: device.width, h: device.height };
}

function scaleFor(w: number, h: number, zoom: ZoomLevel, mode: ViewMode): number {
  if (zoom === "50") return 0.5;
  if (zoom === "75") return 0.75;
  if (zoom === "100") return 1;
  const maxH = mode === "focus" ? 720 : mode === "compare" ? 560 : 420;
  const maxW = mode === "focus" ? 520 : mode === "compare" ? 360 : 260;
  return Math.min(maxW / w, maxH / h, 1);
}

export class DeviceGrid {
  readonly el: HTMLElement;
  private srcdoc = buildSrcDoc("");
  private observer: IntersectionObserver;
  private resizeObserver: ResizeObserver;
  private view: GridView | undefined;
  private onFocus: (id: string) => void;

  constructor(onFocus: (id: string) => void) {
    this.onFocus = onFocus;
    this.el = document.createElement("div");
    this.el.className = "hdp-grid";
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.el);
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const iframe = entry.target.querySelector("iframe");
          if (iframe) applySrcDoc(iframe, this.srcdoc);
        }
      },
      { root: null, rootMargin: "240px", threshold: 0.01 },
    );
  }

  setHtml(html: string): void {
    this.srcdoc = buildSrcDoc(html);
    for (const iframe of this.el.querySelectorAll("iframe")) {
      const card = iframe.closest(".hdp-device");
      if (card && this.isNear(card)) applySrcDoc(iframe, this.srcdoc);
    }
  }

  setView(view: GridView): void {
    this.view = view;
    const shown = this.resolve(view);
    this.el.dataset.mode = view.mode;
    const shownIds = new Set(shown.map(device => device.id));
    for (const card of this.el.querySelectorAll<HTMLElement>(".hdp-device")) card.hidden = !shownIds.has(card.dataset.id!);
    this.el.querySelector(".hdp-empty-grid")?.remove();

    if (shown.length === 0) {
      const message = document.createElement("div");
      message.className = "hdp-empty-grid";
      message.textContent = "No devices selected. Open Catalog to add viewports.";
      this.el.append(message);
      return;
    }

    for (const device of shown) {
      const { w, h } = viewport(device, view.orientation);
      const pad = device.category === "ipad" || device.category === "android-tablet" ? 14 : 11;
      const scale = scaleFor(w + pad * 2, h + pad * 2, view.zoom, view.mode);
      let card = Array.from(this.el.querySelectorAll<HTMLElement>(".hdp-device")).find(card => card.dataset.id === device.id);
      if (card) {
        card.hidden = false;
        card.style.order = String(shown.indexOf(device));
        card.classList.toggle("is-focus", device.id === view.focusId);
        card.dataset.width = String(w + pad * 2);
        card.dataset.height = String(h + pad * 2);
        card.querySelector<HTMLElement>(".hdp-device__bezel")!.style.width = `${w + pad * 2}px`;
        card.querySelector<HTMLElement>(".hdp-device__bezel")!.style.height = `${h + pad * 2}px`;
        card.querySelector(".hdp-device__spec")!.textContent = `${w}×${h} · @${device.pixelRatio}x`;
        if (this.isNear(card)) applySrcDoc(card.querySelector("iframe")!, this.srcdoc);
        continue;
      }
      card = document.createElement("article");
      card.style.order = String(shown.indexOf(device));
      card.className = "hdp-device is-loading";
      card.setAttribute("aria-busy", "true");
      if (device.id === view.focusId) card.classList.add("is-focus");
      card.dataset.id = device.id;
      card.dataset.width = String(w + pad * 2);
      card.dataset.height = String(h + pad * 2);
      card.dataset.os = device.os;
      card.dataset.notch = device.notch;
      card.dataset.family = device.os === "browser" ? "browser" : device.category.includes("tablet") || device.category === "ipad"
        ? "tablet"
        : "phone";
      card.innerHTML = `
        <header class="hdp-device__meta">
          <span class="hdp-device__name">${escapeHtml(device.name)}</span>
          <span class="hdp-device__spec">${w}×${h} · @${device.pixelRatio}x</span>
        </header>
        <div class="hdp-device__stage" style="width:${Math.round((w + pad * 2) * scale)}px;height:${Math.round((h + pad * 2) * scale)}px">
          <div class="hdp-preview-loading" role="status"><span class="hdp-spinner" aria-hidden="true"></span><span>Loading preview…</span></div>
          <div class="hdp-device__bezel" style="width:${w + pad * 2}px;height:${h + pad * 2}px;padding:${pad}px;transform:scale(${scale})">
            <div class="hdp-device__screen">
              <div class="hdp-device__chrome" aria-hidden="true">
                <span class="hdp-device__island"></span>
                <span class="hdp-device__notch"></span>
                <span class="hdp-device__punch"></span>
                <span class="hdp-device__button"></span>
                <span class="hdp-device__home"></span>
              </div>
              <iframe class="hdp-device__frame"></iframe>
            </div>
          </div>
        </div>
      `;
      const heading = card.querySelector<HTMLElement>(".hdp-device__meta")!;
      heading.tabIndex = 0;
      heading.setAttribute("role", "button");
      heading.setAttribute("aria-label", `Focus ${device.name}`);
      heading.addEventListener("click", () => this.onFocus(device.id));
      heading.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") { event.preventDefault(); this.onFocus(device.id); }
      });
      this.el.append(card);
      this.observer.observe(card);
    }
    this.resize();
  }

  private resize(): void {
    if (!this.view) return;
    const available = Math.max(100, this.el.clientWidth - 34);
    for (const card of this.el.querySelectorAll<HTMLElement>(".hdp-device")) {
      const w = Number(card.dataset.width);
      const h = Number(card.dataset.height);
      const preferred = scaleFor(w, h, this.view.zoom, this.view.mode);
      const scale = this.view.zoom === "fit" ? Math.min(preferred, available / w) : preferred;
      const stage = card.querySelector<HTMLElement>(".hdp-device__stage")!;
      stage.style.width = `${Math.round(w * scale)}px`;
      stage.style.height = `${Math.round(h * scale)}px`;
      card.querySelector<HTMLElement>(".hdp-device__bezel")!.style.transform = `scale(${scale})`;
    }
  }

  destroy(): void {
    this.observer.disconnect();
    this.resizeObserver.disconnect();
    this.el.replaceChildren();
  }

  private resolve(view: GridView): PreviewViewport[] {
    if (view.mode === "focus") {
      const current =
        view.devices.find((d) => d.id === view.focusId) ?? view.devices[0];
      return current ? [current] : [];
    }
    if (view.mode === "compare") {
      return view.compareIds.flatMap(id => { const device = view.devices.find(d => d.id === id); return device ? [device] : []; });
    }
    return view.devices;
  }

  private isNear(node: Element): boolean {
    if ((node as HTMLElement).hidden) return false;
    const rect = node.getBoundingClientRect();
    return rect.bottom > -240 && rect.top < window.innerHeight + 240;
  }
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
